import { Project } from '../pages/Gallery';

export interface ICloningJob {
	jobId: string;
	sourceProjectId: string;
	sourceProject: Project;
	clonedProjectId?: string;
	workspaceId: string;
	userId: string;
	startedAt: number;
	progress: number;
	latestEvent: string;
	status: 'in_progress' | 'completed' | 'failed';
}

const STORAGE_KEY = 'elyx_cloning_jobs';
const STALE_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes

const getStorageKey = (workspaceId?: string, userId?: string) => {
	if (workspaceId && userId) {
		return `${STORAGE_KEY}:${workspaceId}:${userId}`;
	}
	return STORAGE_KEY;
};

export const saveCloningJob = (job: ICloningJob): void => {
	try {
		const key = getStorageKey(job.workspaceId, job.userId);
		const existingJobs = getAllCloningJobs(job.workspaceId, job.userId);
		const updatedJobs = existingJobs.filter((j) => j.jobId !== job.jobId);
		updatedJobs.push(job);
		localStorage.setItem(key, JSON.stringify(updatedJobs));
	} catch (error) {
		console.error('Failed to save cloning job to localStorage', error);
	}
};

export const getAllCloningJobs = (
	workspaceId?: string,
	userId?: string
): ICloningJob[] => {
	try {
		const key = getStorageKey(workspaceId, userId);
		const raw = localStorage.getItem(key);
		if (!raw) return [];
		const jobs: ICloningJob[] = JSON.parse(raw);
		const now = Date.now();
		// Return non-stale jobs
		return jobs.filter((job) => now - job.startedAt < STALE_TIMEOUT_MS);
	} catch (error) {
		console.error('Failed to read cloning jobs from localStorage', error);
		return [];
	}
};

export const getInProgressCloningJobs = (
	workspaceId?: string,
	userId?: string
): ICloningJob[] => {
	return getAllCloningJobs(workspaceId, userId).filter(
		(job) => job.status === 'in_progress'
	);
};

export const removeCloningJob = (
	jobId: string,
	workspaceId?: string,
	userId?: string
): void => {
	try {
		const key = getStorageKey(workspaceId, userId);
		const existingJobs = getAllCloningJobs(workspaceId, userId);
		const updatedJobs = existingJobs.filter((j) => j.jobId !== jobId);
		localStorage.setItem(key, JSON.stringify(updatedJobs));
	} catch (error) {
		console.error('Failed to remove cloning job from localStorage', error);
	}
};

export const pruneStaleCloningJobs = (
	workspaceId?: string,
	userId?: string
): void => {
	try {
		const key = getStorageKey(workspaceId, userId);
		const existingJobs = getAllCloningJobs(workspaceId, userId);
		localStorage.setItem(key, JSON.stringify(existingJobs));
	} catch (error) {
		console.error('Failed to prune stale cloning jobs', error);
	}
};
